/**
 * ConfirmDialog Component
 *
 * A reusable confirmation dialog for destructive actions.
 * Consistent styling across the application.
 *
 * @module components/common/ConfirmDialog
 */

import { memo } from 'react';
import { Trash2, AlertTriangle, Loader2 } from 'lucide-react';

/**
 * ConfirmDialog Component
 *
 * @param {object} props - Component props
 * @param {boolean} props.isOpen - Whether the dialog is visible
 * @param {string} props.title - Dialog title
 * @param {string} props.itemName - Name of the item being deleted/actioned
 * @param {string} [props.message] - Custom message (default: "Are you sure you want to delete...")
 * @param {string} [props.warning] - Optional warning text shown below message
 * @param {string} [props.confirmLabel='Delete'] - Label for confirm button
 * @param {string} [props.cancelLabel='Cancel'] - Label for cancel button
 * @param {'danger'|'warning'} [props.variant='danger'] - Button color variant
 * @param {function} props.onConfirm - Confirm callback
 * @param {function} props.onCancel - Cancel callback
 * @param {boolean} [props.isProcessing=false] - Whether action is in progress
 * @returns {React.ReactElement|null} Dialog element or null
 */
const ConfirmDialog = memo(function ConfirmDialog({
  isOpen,
  title,
  itemName,
  message,
  warning,
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  variant = 'danger',
  onConfirm,
  onCancel,
  isProcessing = false
}) {
  if (!isOpen) return null;

  const Icon = variant === 'danger' ? Trash2 : AlertTriangle;
  const iconColor = variant === 'danger' ? 'text-red-400' : 'text-amber-400';
  const buttonColor = variant === 'danger'
    ? 'bg-red-600 hover:bg-red-500'
    : 'bg-amber-600 hover:bg-amber-500';

  const defaultMessage = `Are you sure you want to delete "${itemName}"?`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Icon className={`w-5 h-5 ${iconColor}`} />
            {title}
          </h3>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          <p className="text-gray-300">
            {message || defaultMessage}
          </p>
          {warning && (
            <p className="text-sm text-amber-400 mt-3">
              {warning}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="px-6 py-4 border-t border-gray-700 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isProcessing}
            className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isProcessing}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white ${buttonColor} rounded-lg transition-colors disabled:opacity-50`}
          >
            {isProcessing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Icon className="w-4 h-4" />
            )}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
});

export default ConfirmDialog;
